import { CSSProperties, useState } from 'react'
import styled, { css } from 'styled-components'

import { Icon } from '../../../components/Icon'
import WysiwygContent from '../../../components/WysiwygContent'
import { formatCurrencyValue } from '../../../helpers'
import { IArtField, TFrameValueMode } from '../../../services/Contests/interfaces'
import { magenta } from '../../../theme'
import { iconForArtField } from './artFieldHelpers'

/*
 * Faithful preview of the public art-submission form (ABRA-84).
 *
 * The styled primitives below are copied 1:1 from the public site
 * (site-abra-v2 · concurso/styles/contest.styles.ts + ArtSubmissionModal) so a
 * person configuring the questions in the panel sees exactly how the form will
 * look to the student. It is interactive (you can type/select) but does not
 * submit anything — image upload is a static placeholder.
 */

const PINK = magenta[500]

const Frame = styled.div`
  background: #111111;
  border: 1px solid #292929;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  color: rgba(255, 255, 255, 0.7);
  font-family: var(--font-georama), 'Georama', sans-serif;
`

const FrameHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #292929;
`

const FrameTitle = styled.span`
  color: #fff;
  font-weight: 700;
  font-size: 1.25rem;
`

const FrameBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 20px 24px;
`

const FrameFooter = styled.div`
  padding: 16px 24px 20px;
  border-top: 1px solid #292929;
  display: flex;
  gap: 12px;
`

const fieldCss = css<{ $hasError?: boolean }>`
  background-color: transparent;
  border: 1px solid #292929;
  border-radius: 8px;
  padding: 10px 14px;
  color: rgba(255, 255, 255, 0.85);
  width: 100%;
  font-size: 0.9375rem;
  font-family: var(--font-georama), 'Georama', sans-serif;
  outline: none;
  transition: border-color 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
  &:focus {
    border-color: ${PINK};
  }
  &:hover:not(:focus) {
    border-color: ${PINK};
  }
`

const DarkInput = styled.input`
  ${fieldCss};
  height: 44px;
`

const DarkTextarea = styled.textarea`
  ${fieldCss};
  min-height: 80px;
  resize: vertical;
`

const DarkSelect = styled.select`
  ${fieldCss};
  height: 44px;
  background-color: #0f0f0f;
  cursor: pointer;
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23fa0069' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 14px center;
  padding-right: 36px;

  option {
    background-color: #1a1a1a;
    color: #fff;
  }
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`

const FieldLabel = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  font-size: 0.9375rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 4px;
`

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #292929;
  width: 100%;
  margin: 0;
`

const ImageUploadArea = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 160px;
  height: 160px;
  border-radius: 10%;
  border: 2px dashed #fa006860;
  background-color: #222222;
  text-align: center;
  color: rgba(255, 255, 255, 0.5);
`

const ToggleTrack = styled.div<{ $checked: boolean }>`
  width: 48px;
  height: 26px;
  border-radius: 13px;
  background-color: ${({ $checked }) => ($checked ? PINK : '#292929')};
  position: relative;
  transition: background-color 0.2s ease;
  flex-shrink: 0;
  cursor: pointer;
`

const ToggleThumb = styled.div<{ $checked: boolean }>`
  position: absolute;
  top: 3px;
  left: ${({ $checked }) => ($checked ? '25px' : '3px')};
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: white;
  transition: left 0.2s ease;
`

const RangeSlider = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  background: linear-gradient(
    to right,
    ${PINK} 0%,
    ${PINK}
      ${({ value, min, max }) => {
        const val = Number(value ?? 100)
        const minVal = Number(min ?? 100)
        const maxVal = Number(max ?? 650)
        return `${((val - minVal) / (maxVal - minVal)) * 100}%`
      }},
    #292929
      ${({ value, min, max }) => {
        const val = Number(value ?? 100)
        const minVal = Number(min ?? 100)
        const maxVal = Number(max ?? 650)
        return `${((val - minVal) / (maxVal - minVal)) * 100}%`
      }},
    #292929 100%
  );
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${PINK};
    cursor: pointer;
    box-shadow: 0 0 6px rgba(250, 0, 105, 0.5);
  }
  &::-moz-range-thumb {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: ${PINK};
    cursor: pointer;
    border: none;
  }
`

const PinkButton = styled.div`
  background: linear-gradient(to bottom right, ${PINK}, #ac004b);
  color: #fff;
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  opacity: 0.85;
`

const OutlineButton = styled.div`
  color: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  padding: 12px 24px;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`

const AUCTION_MIN = 100
const AUCTION_MAX = 650
const AUCTION_STEP = 50
const MARKS = [100, 150, 200, 250, 300, 350, 400, 450, 500, 550, 600, 650]

// The global input reset on the site forces radios/checkboxes full-width; the
// public form opts out with this — we mirror it for visual parity.
const CHOICE_INPUT_STYLE: CSSProperties = {
  accentColor: PINK,
  width: 18,
  height: 18,
  minWidth: 18,
  padding: 0,
  margin: 0,
  flexShrink: 0
}

interface IArtFieldsPreviewProps {
  fields: IArtField[]
  notice?: string | null
  frameValueMode?: TFrameValueMode
  /** ABRA-337 — fixed starting bid of the contest; replaces the slider when set. */
  defaultStartingBid?: number | null
}

export default function ArtFieldsPreview({
  fields,
  notice,
  frameValueMode = 'disabled',
  defaultStartingBid = null
}: IArtFieldsPreviewProps) {
  // Local, throwaway state purely so the preview feels real (typing/selecting).
  const [values, setValues] = useState<Record<string, string>>({})
  const [auctionOn, setAuctionOn] = useState(false)
  const [startingBid, setStartingBid] = useState(AUCTION_MIN)
  const [frameValue, setFrameValue] = useState<number | null>(null)
  const effectiveStartingBid = defaultStartingBid ?? startingBid

  const setFieldValue = (key: string, value: string) => setValues((prev) => ({ ...prev, [key]: value }))

  const renderFieldInput = (field: IArtField) => {
    const value = values[field.key] ?? ''
    const options = field.options ?? []

    const textInput = (
      <DarkInput
        value={value}
        placeholder={field.placeholder || undefined}
        maxLength={255}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue(field.key, e.target.value)}
      />
    )

    switch (field.type) {
      case 'textarea':
        return (
          <DarkTextarea
            rows={3}
            value={value}
            placeholder={field.placeholder || undefined}
            maxLength={2000}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setFieldValue(field.key, e.target.value)}
          />
        )
      case 'number':
        return (
          <DarkInput
            type="number"
            value={value}
            placeholder={field.placeholder || undefined}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue(field.key, e.target.value)}
          />
        )
      case 'select':
        if (options.length === 0) return textInput
        return (
          <DarkSelect
            value={value}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setFieldValue(field.key, e.target.value)}
          >
            <option value="">Selecione...</option>
            {options.map((option, i) => (
              <option key={`${option}-${i}`} value={option}>
                {option || `Opção ${i + 1}`}
              </option>
            ))}
          </DarkSelect>
        )
      case 'radio':
        if (options.length === 0) return textInput
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {options.map((option, i) => (
              <label
                key={`${option}-${i}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  color: 'rgba(255,255,255,0.85)',
                  fontSize: '0.9375rem'
                }}
              >
                <input
                  type="radio"
                  name={field.key}
                  value={option}
                  checked={value === option}
                  onChange={() => setFieldValue(field.key, option)}
                  style={CHOICE_INPUT_STYLE}
                />
                {option || `Opção ${i + 1}`}
              </label>
            ))}
          </div>
        )
      case 'checkbox':
        return (
          <label
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 10,
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.9375rem',
              lineHeight: 1.5
            }}
          >
            <input
              type="checkbox"
              checked={value === '1'}
              onChange={(e) => setFieldValue(field.key, e.target.checked ? '1' : '0')}
              style={{ ...CHOICE_INPUT_STYLE, marginTop: 3 }}
            />
            <span style={{ flex: 1 }}>
              {field.label || <em style={{ opacity: 0.5 }}>(sem rótulo)</em>}
              {field.required && <span style={{ color: PINK }}> *</span>}
            </span>
          </label>
        )
      default:
        return textInput
    }
  }

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Adicionar Obra</FrameTitle>
        <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.25rem' }}>✕</span>
      </FrameHeader>

      <FrameBody>
        {/* Image upload (static placeholder) */}
        <FieldGroup style={{ alignItems: 'center' }}>
          <FieldLabel>
            <Icon name="lni lni-image" color={PINK} />
            Imagem da Obra
          </FieldLabel>
          <ImageUploadArea>
            <Icon name="lni lni-upload" size="xl" />
            <div style={{ marginTop: 8, padding: '0 12px', fontSize: '0.8rem' }}>Clique para selecionar a imagem</div>
            <div style={{ fontSize: '0.75rem', marginTop: 4 }}>JPG ou PNG — máx. 40MB</div>
          </ImageUploadArea>
          <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>Limite de 40MB - jpg, jpeg, png</span>
        </FieldGroup>

        {/* Discreet contest notice */}
        {notice && notice.trim() && (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              padding: '10px 12px',
              borderRadius: 10,
              border: '1px solid rgba(250,0,105,0.35)',
              background: 'rgba(250,0,105,0.07)'
            }}
          >
            <Icon name="lni lni-warning" color={PINK} mt="2px" />
            <WysiwygContent
              content={notice}
              fontSize="0.8125rem"
              color="rgba(255,255,255,0.75)"
              lineHeight="1.45"
              sx={{ '& p': { margin: 0 }, '& p + p': { marginTop: 2 }, '& ul, & ol': { my: 1, pl: 4 } }}
            />
          </div>
        )}

        {/* Fixed artwork title — always present */}
        <FieldGroup>
          <FieldLabel>
            <Icon name="lni lni-pencil" color={PINK} />
            Título da obra
            <span style={{ color: PINK }}> *</span>
          </FieldLabel>
          <DarkInput
            value={values['name'] ?? ''}
            placeholder="Ex: Sonho de Verão"
            maxLength={255}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('name', e.target.value)}
          />
        </FieldGroup>

        {/* Customizable artwork data fields */}
        {fields.map((field) => (
          <FieldGroup key={field.key}>
            {field.type !== 'checkbox' && (
              <FieldLabel>
                <i className={iconForArtField(field)} style={{ color: PINK }} />
                {field.label || <em style={{ opacity: 0.5 }}>(sem rótulo)</em>}
                {field.required && <span style={{ color: PINK }}> *</span>}
              </FieldLabel>
            )}
            {renderFieldInput(field)}
          </FieldGroup>
        ))}

        <Divider />

        {/* Auction toggle */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: '1px solid #c2c2c2',
            padding: 16,
            borderRadius: 12,
            gap: 12
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem' }}>
              Você deseja que sua obra esteja a venda?
            </span>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
              Permitir que sua obra seja leiloada no evento
            </span>
          </div>
          <ToggleTrack $checked={auctionOn} onClick={() => setAuctionOn((v) => !v)}>
            <ToggleThumb $checked={auctionOn} />
          </ToggleTrack>
        </div>

        {auctionOn && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* ABRA-337 — with a fixed starting bid the applicant no longer picks it:
                the site swaps the slider for this notice. */}
            {defaultStartingBid != null ? (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: '10px 12px',
                  borderRadius: 10,
                  border: '1px solid rgba(250,0,105,0.35)',
                  background: 'rgba(250,0,105,0.07)'
                }}
              >
                <Icon name="lni lni-tag" color={PINK} mt="2px" />
                <span style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.45 }}>
                  O lance inicial das obras deste concurso é definido pela organização:{' '}
                  <strong style={{ color: PINK }}>R$ {formatCurrencyValue(defaultStartingBid)}</strong>
                </span>
              </div>
            ) : (
              <FieldGroup>
                <FieldLabel>
                  <Icon name="lni lni-tag" color={PINK} />
                  Lance inicial: <span style={{ fontWeight: 'bold', color: PINK }}>R$ {startingBid},00</span>
                </FieldLabel>
                <div style={{ padding: '0 10px' }}>
                  <RangeSlider
                    min={AUCTION_MIN}
                    max={AUCTION_MAX}
                    step={AUCTION_STEP}
                    value={startingBid}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStartingBid(Number(e.target.value))}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                    {MARKS.map((mark) => (
                      <span key={mark} style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.45)' }}>
                        {mark}
                      </span>
                    ))}
                  </div>
                </div>
              </FieldGroup>
            )}

            {/* ABRA-191 — only asked of the applicant in the "per_art" mode.
                Mirrors the real submission form, asterisk and hint included, so
                the team sees exactly what the applicant will see. */}
            {frameValueMode === 'per_art' && (
              <FieldGroup>
                <FieldLabel>
                  <Icon name="lni lni-frame-expand" color={PINK} />
                  Valor da moldura
                  <span style={{ color: PINK }}> *</span>
                </FieldLabel>
                {/* Esta é a prévia de como o formulário aparece no site público, com a
                    aparência de lá — por isso o `R$` fica embutido no valor em vez de vir de
                    um `<MoneyInput>`, que é um campo do painel. A máscara, porém, é a mesma. */}
                <DarkInput
                  value={frameValue == null ? '' : `R$ ${formatCurrencyValue(frameValue)}`}
                  placeholder="R$ 0,00"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const digits = e.target.value.replace(/\D/g, '')
                    setFrameValue(digits === '' ? null : Number(digits) / 100)
                  }}
                />
                <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
                  Informe quanto custa a moldura da sua obra. O lance do leilão não inclui a moldura.
                </span>
              </FieldGroup>
            )}

            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: '1px solid #fa006852',
                backgroundColor: '#fff2f7',
                padding: 16,
                borderRadius: 12
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'row', gap: 8 }}>
                <Icon name="lni lni-warning" color={PINK} />
                <span style={{ fontSize: '0.75rem', color: '#1a1a1a', lineHeight: 1.5 }}>
                  Estou ciente de que minha obra será leiloada na abertura da mostra com lance inicial de{' '}
                  <strong>R$ {formatCurrencyValue(effectiveStartingBid)}</strong>. Concordo que, em caso de venda, 40%
                  do valor final será retido pela Galeria ABRA a título de comissão.
                </span>
              </div>
            </div>
          </div>
        )}
      </FrameBody>

      <FrameFooter>
        <OutlineButton>Cancelar</OutlineButton>
        <PinkButton>Adicionar Obra</PinkButton>
      </FrameFooter>
    </Frame>
  )
}
